/**
 * Validación reutilizable de campos (markup en _forms.less: .form-field-group + .msg-error)
 * =============================================================================
 * Uso en HTML:
 *   <div class="form-field-group">
 *     <label for="x">...</label>
 *     <div class="form-field-group__control">
 *       <input id="x" data-validate="required number" data-validate-min="0" data-validate-max="100">
 *       <p class="msg-error" role="alert" hidden aria-hidden="true"></p>
 *     </div>
 *   </div>
 *
 * Reglas (data-validate, separadas por espacio): required | number | letters | email
 * Límites opcionales: data-validate-min, data-validate-max (números)
 * Mensajes custom: data-msg-required, data-msg-number, etc.
 */

const DEFAULT_MESSAGES = {
  required: 'Este campo es obligatorio.',
  number: 'Solo se permiten números.',
  letters: 'Solo se permiten letras.',
  email: 'Introduce un correo electrónico válido.',
  min: 'El valor mínimo es {min}.',
  max: 'El valor máximo es {max}.',
  range: 'El valor debe estar entre {min} y {max}.',
};

const LETTERS_RE = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getGroup(control) {
  return control?.closest('.form-field-group') ?? null;
}

function getMsgEl(group) {
  return (
    group?.querySelector('.form-field-group__control .msg-error') ??
    group?.querySelector('.msg-error') ??
    null
  );
}

function getControlEl(group) {
  const slot = group?.querySelector('.form-field-group__control');
  if (slot) {
    return (
      slot.querySelector('[data-validate]') ??
      slot.querySelector('input, select, textarea')
    );
  }
  return (
    group?.querySelector('[data-validate]') ??
    group?.querySelector('input, select, textarea')
  );
}

function getRules(control) {
  const raw = control.getAttribute('data-validate') || '';
  return raw
    .split(/\s+/)
    .map((r) => r.trim())
    .filter(Boolean);
}

function getCustomMessage(control, key) {
  const attr = control.getAttribute(`data-msg-${key}`);
  return attr && attr.trim() ? attr.trim() : null;
}

function formatMessage(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ''));
}

function isEmpty(control, value) {
  if (control.type === 'checkbox' || control.type === 'radio') {
    return !control.checked;
  }
  if (control.tagName === 'SELECT') {
    return value === '' || value == null;
  }
  return value === '';
}

function validateControl(control) {
  const rules = getRules(control);
  if (!rules.length) {
    return '';
  }

  const raw = control.tagName === 'SELECT' ? control.value : String(control.value ?? '').trim();
  const empty = isEmpty(control, raw);

  if (rules.includes('required') && empty) {
    return getCustomMessage(control, 'required') || DEFAULT_MESSAGES.required;
  }

  if (empty) {
    return '';
  }

  if (rules.includes('number')) {
    const normalized = raw.replace(',', '.');
    if (Number.isNaN(Number(normalized))) {
      return getCustomMessage(control, 'number') || DEFAULT_MESSAGES.number;
    }
  }

  if (rules.includes('letters') && !LETTERS_RE.test(raw)) {
    return getCustomMessage(control, 'letters') || DEFAULT_MESSAGES.letters;
  }

  if (rules.includes('email') && !EMAIL_RE.test(raw)) {
    return getCustomMessage(control, 'email') || DEFAULT_MESSAGES.email;
  }

  if (rules.includes('number') || control.type === 'number') {
    const num = parseFloat(raw.replace(',', '.'));
    if (Number.isNaN(num)) {
      return getCustomMessage(control, 'number') || DEFAULT_MESSAGES.number;
    }

    const minAttr = control.getAttribute('data-validate-min');
    const maxAttr = control.getAttribute('data-validate-max');
    const min = minAttr !== null && minAttr !== '' ? parseFloat(minAttr) : null;
    const max = maxAttr !== null && maxAttr !== '' ? parseFloat(maxAttr) : null;

    if (min !== null && !Number.isNaN(min) && max !== null && !Number.isNaN(max)) {
      if (num < min || num > max) {
        return (
          getCustomMessage(control, 'range') ||
          formatMessage(DEFAULT_MESSAGES.range, { min, max })
        );
      }
    } else if (min !== null && !Number.isNaN(min) && num < min) {
      return (
        getCustomMessage(control, 'min') || formatMessage(DEFAULT_MESSAGES.min, { min })
      );
    } else if (max !== null && !Number.isNaN(max) && num > max) {
      return (
        getCustomMessage(control, 'max') || formatMessage(DEFAULT_MESSAGES.max, { max })
      );
    }
  }

  return '';
}

function setGroupState(group, message) {
  if (!group) return;

  const control = getControlEl(group);
  const msgEl = getMsgEl(group);

  if (message) {
    group.classList.add('is-invalid');
    if (msgEl) {
      msgEl.textContent = message;
      msgEl.hidden = false;
      msgEl.setAttribute('aria-hidden', 'false');
    }
    control?.setAttribute('aria-invalid', 'true');
    if (msgEl && control) {
      const id = msgEl.id || `${control.id || control.name}-error`;
      if (!msgEl.id) msgEl.id = id;
      control.setAttribute('aria-describedby', id);
    }
  } else {
    group.classList.remove('is-invalid');
    if (msgEl) {
      msgEl.textContent = '';
      msgEl.hidden = true;
      msgEl.setAttribute('aria-hidden', 'true');
    }
    control?.removeAttribute('aria-invalid');
    if (control) {
      const describedBy = control.getAttribute('aria-describedby');
      const msgId = msgEl?.id;
      if (describedBy && msgId && describedBy === msgId) {
        control.removeAttribute('aria-describedby');
      }
    }
  }
}

/**
 * Valida un control y actualiza su .form-field-group.
 * @returns {boolean} true si válido
 */
export function validateField(control) {
  const group = getGroup(control);
  if (!group) return true;

  const message = validateControl(control);
  setGroupState(group, message);
  return !message;
}

function getValidatableControls(root) {
  return Array.from(
    root.querySelectorAll('.form-field-group [data-validate]')
  );
}

/**
 * @param {HTMLElement} root form o contenedor
 * @param {{ validateOnInput?: boolean }} [options]
 */
export function initFormFieldValidation(root, options = {}) {
  const { validateOnInput = true } = options;
  const controls = getValidatableControls(root);

  const onBlur = (ev) => {
    const el = ev.target.closest('[data-validate]');
    if (el && root.contains(el)) validateField(el);
  };

  const onInput = (ev) => {
    const el = ev.target.closest('[data-validate]');
    if (!el || !root.contains(el)) return;
    const group = getGroup(el);
    if (group?.classList.contains('is-invalid')) {
      validateField(el);
    }
  };

  root.addEventListener('blur', onBlur, true);
  if (validateOnInput) {
    root.addEventListener('input', onInput, true);
    root.addEventListener('change', onInput, true);
  }

  return {
    validateAll() {
      let ok = true;
      getValidatableControls(root).forEach((control) => {
        if (!validateField(control)) ok = false;
      });
      return ok;
    },
    isValid() {
      return getValidatableControls(root).every((control) => !validateControl(control));
    },
    validateField,
  };
}

export { DEFAULT_MESSAGES };
